import { useState, useEffect } from 'react';
import { usePagination } from '@/hooks/usePagination';
import { useTranslation } from 'react-i18next';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Pencil, Trash2, Filter, Search } from 'lucide-react';
import type { CommunityMember, CommunityMemberFilters } from '../../../../shared/types';

interface CommunityMapTableProps {
  members: CommunityMember[];
  isLoading: boolean;
  onEdit: (member: CommunityMember) => void;
  onDelete?: (memberId: string) => void;
  filters: CommunityMemberFilters;
  onFiltersChange: (filters: CommunityMemberFilters) => void;
}

export function CommunityMapTable({
  members,
  isLoading,
  onEdit,
  onDelete,
  filters,
  onFiltersChange,
}: CommunityMapTableProps) {
  const { t } = useTranslation();
  const [showFilters, setShowFilters] = useState(false);
  const [searchInput, setSearchInput] = useState(filters.search || '');

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      updateFilter('search', searchInput || undefined);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchInput]);

  // Sync search input with filters from parent
  useEffect(() => {
    if (filters.search !== searchInput) {
      setSearchInput(filters.search || '');
    }
  }, [filters.search]);

  const updateFilter = (key: keyof CommunityMemberFilters, value: string[] | string | undefined) => {
    onFiltersChange({ ...filters, [key]: value });
  };

  // Get unique values for filters
  const uniqueCountries = [...new Set(members.map((m) => m.country))].sort();
  const uniqueClapbases = [...new Set(members.map((m) => m.clapbase))].sort();
  const uniqueTeams = [...new Set(members.map((m) => m.team).filter(Boolean))].sort();
  const uniqueAreas = [...new Set(members.map((m) => m.area).filter(Boolean))].sort();

  // 350 personas × 19 columnas montaban ~6.650 celdas de una sola vez
  const { pageItems: pageMembers, footer: paginationFooter } = usePagination(members, 25);

  if (isLoading) {
    return (
      <div className="p-8 text-center text-muted-foreground">
        {t('users.community.loading')}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Search and Filter Toggle */}
      <div className="flex gap-4 p-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder={t('users.community.search_placeholder')}
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className="pl-10"
          />
        </div>
        <Button
          variant={showFilters ? 'default' : 'outline'}
          onClick={() => setShowFilters(!showFilters)}
        >
          <Filter className="mr-2 h-4 w-4" />
          {t('users.community.filters')}
        </Button>
      </div>

      {/* Filter Row */}
      {showFilters && (
        <div className="grid gap-4 p-4 bg-muted/30 rounded-lg md:grid-cols-5">
          <div>
            <label className="text-sm font-medium mb-2 block">{t('users.community.status')}</label>
            <select
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              value={filters.status?.[0] || ''}
              onChange={(e) => updateFilter('status', e.target.value ? [e.target.value] : undefined)}
            >
              <option value="">Todos</option>
              <option value="Activo">Activo</option>
              <option value="Inactivo">Inactivo</option>
              <option value="Baja">Baja</option>
            </select>
          </div>

          <div>
            <label className="text-sm font-medium mb-2 block">{t('users.community.country')}</label>
            <select
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              value={filters.country?.[0] || ''}
              onChange={(e) => updateFilter('country', e.target.value ? [e.target.value] : undefined)}
            >
              <option value="">{t('users.community.all_m')}</option>
              {uniqueCountries.map((country) => (
                <option key={country} value={country}>
                  {country}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="text-sm font-medium mb-2 block">{t('users.community.clapbase')}</label>
            <select
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              value={filters.clapbase?.[0] || ''}
              onChange={(e) =>
                updateFilter('clapbase', e.target.value ? [e.target.value] : undefined)
              }
            >
              <option value="">{t('users.community.all_f')}</option>
              {uniqueClapbases.map((clapbase) => (
                <option key={clapbase} value={clapbase}>
                  {clapbase}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="text-sm font-medium mb-2 block">{t('users.community.team')}</label>
            <select
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              value={filters.team?.[0] || ''}
              onChange={(e) => updateFilter('team', e.target.value ? [e.target.value] : undefined)}
            >
              <option value="">{t('users.community.all_m')}</option>
              {uniqueTeams.map((team) => (
                <option key={team} value={team}>
                  {team}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="text-sm font-medium mb-2 block">{t('users.community.area')}</label>
            <select
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              value={filters.area?.[0] || ''}
              onChange={(e) => updateFilter('area', e.target.value ? [e.target.value] : undefined)}
            >
              <option value="">{t('users.community.all_f')}</option>
              {uniqueAreas.map((area) => (
                <option key={area} value={area}>
                  {area}
                </option>
              ))}
            </select>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="rounded-md border overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="min-w-[60px]">{t('users.community.table.id')}</TableHead>
              <TableHead className="min-w-[120px]">{t('users.community.table.name')}</TableHead>
              <TableHead className="min-w-[120px]">{t('users.community.table.last_name')}</TableHead>
              <TableHead className="min-w-[200px]">{t('users.community.table.email')}</TableHead>
              <TableHead className="min-w-[100px]">{t('users.community.table.country')}</TableHead>
              <TableHead className="min-w-[100px]">{t('users.community.table.clapbase')}</TableHead>
              <TableHead className="min-w-[200px]">{t('users.community.table.role')}</TableHead>
              <TableHead className="min-w-[80px]">{t('users.community.table.level')}</TableHead>
              <TableHead className="min-w-[120px]">{t('users.community.table.team')}</TableHead>
              <TableHead className="min-w-[120px]">{t('users.community.table.area')}</TableHead>
              <TableHead className="min-w-[150px]">{t('users.community.table.people_manager')}</TableHead>
              <TableHead className="min-w-[150px]">{t('users.community.table.n1_manager')}</TableHead>
              <TableHead className="min-w-[150px]">{t('users.community.table.responsable_area')}</TableHead>
              <TableHead className="min-w-[100px]">{t('users.community.table.start_date')}</TableHead>
              <TableHead className="min-w-[60px]">{t('users.community.table.month')}</TableHead>
              <TableHead className="min-w-[60px]">{t('users.community.table.year')}</TableHead>
              <TableHead className="min-w-[120px]">{t('users.community.table.birth_date')}</TableHead>
              <TableHead className="min-w-[80px]">{t('users.community.table.status')}</TableHead>
              <TableHead className="text-right min-w-[100px]">{t('users.community.table.actions')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {members.length === 0 ? (
              <TableRow>
                <TableCell colSpan={19} className="h-24 text-center text-muted-foreground">
                  {filters.search || Object.values(filters).some((f) => f && f.length > 0)
                    ? t('users.community.no_results')
                    : t('users.community.no_members')}
                </TableCell>
              </TableRow>
            ) : (
              pageMembers.map((member) => (
                <TableRow key={member.memberId}>
                  <TableCell className="font-mono text-xs">{member.employeeId}</TableCell>
                  <TableCell className="font-medium">{member.name}</TableCell>
                  <TableCell className="font-medium">{member.lastName}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">{member.email}</TableCell>
                  <TableCell>{member.country}</TableCell>
                  <TableCell>{member.clapbase}</TableCell>
                  <TableCell className="text-sm">{member.role}</TableCell>
                  <TableCell className="text-sm">{member.level || '-'}</TableCell>
                  <TableCell className="text-sm">{member.team || '-'}</TableCell>
                  <TableCell className="text-sm">{member.area || '-'}</TableCell>
                  <TableCell className="text-sm">{member.peopleManager || '-'}</TableCell>
                  <TableCell className="text-sm">{member.n1Manager || '-'}</TableCell>
                  <TableCell className="text-sm">{member.responsableArea || '-'}</TableCell>
                  <TableCell className="text-sm">{member.startDate || '-'}</TableCell>
                  <TableCell className="text-sm">{member.startMonth || '-'}</TableCell>
                  <TableCell className="text-sm">{member.startYear || '-'}</TableCell>
                  <TableCell className="text-sm">{member.dateOfBirth || '-'}</TableCell>
                  <TableCell>
                    <Badge
                      variant={
                        member.status === 'Activo'
                          ? 'default'
                          : member.status === 'Inactivo'
                          ? 'secondary'
                          : 'destructive'
                      }
                    >
                      {member.status}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => onEdit(member)}
                        title={t('users.community.edit_title')}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      {onDelete && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => onDelete(member.memberId)}
                          title={t('users.community.delete_title')}
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
      {paginationFooter}

      {/* Results count */}
      {members.length > 0 && (
        <div className="text-sm text-muted-foreground px-4">
          {members.length !== 1
            ? t('users.community.showing_plural', { count: members.length })
            : t('users.community.showing_one', { count: members.length })}
        </div>
      )}
    </div>
  );
}
